/**
 * Which owner a node's `%Name` lands on. `_acquire_unique_name_in_owner` (node.cpp:2222) registers
 * `"%" + name` on the node's owner and refuses to overwrite an entry (node.cpp:2225-2231), so the
 * first claimant in declaration order wins. The seating records the root's claims, and hands each
 * setting another owner holds to the instance node that owner reads it from.
 */
import { boolSlotValue } from '../godot/variant/variantBool.js';
/**
 * The heading's `unique_name_in_owner` as written, or undefined where it writes none. It is read from
 * `rawProperties` alone, which both parsers publish alike (`parser/rawPropertyParity.test.ts`). No
 * slice models the flag, so typed `properties` lacks it. `PROPERTY_USAGE_NO_EDITOR` hides it and
 * still serialises it (node.cpp:4050).
 */
export function uniqueNameSetting(node) {
    return boolSlotValue(node.rawProperties.unique_name_in_owner);
}
/**
 * Who holds a `%Name` once the heading at `path` writes `unique_name_in_owner`: a set takes a free
 * name and the first claimant keeps it (node.cpp:2225-2231), and a clear releases only the node's
 * own name (node.cpp:2236-2249). Undefined where nobody holds it.
 */
export function holderAfterSetting(holder, path, isUnique) {
    if (isUnique)
        return holder ?? path;
    return holder === path ? undefined : holder;
}
/**
 * Whether a node is an instance that hides its sub-scene's owner from the nodes below it. An
 * instanced root is the scene's own root, so only an instance below the root does.
 */
export function isInstanceBelowRoot(node, isRoot) {
    return !isRoot && Boolean(node.instance);
}
/**
 * The seated `owner=` of a heading whose owner path names no node when instantiate sets it. An
 * empty NodePath, which `get_node_or_null` answers with null (node.cpp:1894), so the node gets no
 * owner and its name registers on no table (packed_scene.cpp:159-168, :565-570).
 */
export const NO_OWNER = '';
/**
 * Whether a heading's `owner=` names a node other than the root: the saver writes it only then
 * (resource_format_text.cpp:2024-2025), for a node that instanced content owns.
 */
export function hasForeignOwner(node) {
    return node.owner !== undefined && (node.owner !== '.' || node.ownerContentTrail !== undefined);
}
/**
 * Whether the scene root owns a node, so its `%Name` lands in the root's table. An explicit `owner=`
 * decides. Without one, an override heading inside an instance keeps the sub-scene's owner
 * (resource_format_text.cpp:264-265 zeroes `owner` only when `!(type == TYPE_INSTANTIATED &&
 * instance == -1)`), and every other node is the root's.
 */
export function isOwnedByRoot(node, insideInstance) {
    if (node.owner !== undefined)
        return !hasForeignOwner(node);
    return !(insideInstance && node.overridesExistingNode === true);
}
/**
 * Whether the setting is on a node outside the instance, which its explicit `owner=` gives to
 * the instance. No owner walk applies, since the instance itself holds the name.
 */
export function isOutsideInstance(setting) {
    return setting.pathInInstance.startsWith('../');
}
//# sourceMappingURL=nodeOwner.js.map